import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { Search, Download, Volume2, Eye, Edit } from "lucide-react";
import { toast } from "sonner";
import { wordService } from "@/services/word";
import type { WordItem } from "@/types/word.types";
import { OSSUpload } from "@/components/Upload/index";

// CEFR 等级标签颜色映射
const cefrColors: Record<string, string> = {
  A1: "bg-green-100 text-green-800",
  A2: "bg-blue-100 text-blue-800",
  B1: "bg-yellow-100 text-yellow-800",
  B2: "bg-orange-100 text-orange-800",
  C1: "bg-red-100 text-red-800",
  C2: "bg-purple-100 text-purple-800",
};

export default function WordsManagement() {
  // 分页
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // 数据
  const [loading, setLoading] = useState(true);
  const [words, setWords] = useState<WordItem[]>([]);
  const [total, setTotal] = useState(0);

  // 搜索关键词
  const [keyword, setKeyword] = useState("");

  // 导出弹窗相关状态
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [processingExport, setProcessingExport] = useState(false);

  // 查看详情弹窗
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedWord, setSelectedWord] = useState<WordItem | null>(null);

  // 编辑图片弹窗
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingWord, setEditingWord] = useState<WordItem | null>(null);
  const [imageEditValue, setImageEditValue] = useState<string>("");
  const [uploadingImage, setUploadingImage] = useState(false);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / pageSize)),
    [total, pageSize]
  );

  // UI状态 -> 接口参数映射
  const buildRequestParams = () => {
    const params: any = {
      page: 1110,
      page_size: pageSize,
    };

    // 单词搜索
    const kw = keyword.trim();
    if (kw) {
      params.keyword = kw;
    }

    return params;
  };

  const fetchWords = async () => {
    setLoading(true);
    try {
      const params = buildRequestParams();
      const res = await wordService.getWords(params);

      const list = (res as any).list ?? [];
      const totalCount = (res as any).total ?? 0;

      setWords(list);
      setTotal(totalCount);
    } catch (e) {
      console.error(e);
      toast.error("获取单词列表失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setLoading(false);
    }
  };

  const formatImageUrl = (url: string) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // 假设有一个基础URL前缀
    return `${import.meta.env.VITE_OSS_HOST}Vocabulary_pics/${url}`;
  };

  useEffect(() => {
    fetchWords();
  }, [page, pageSize]);

  const handleSearch = () => {
    setPage(1);
    fetchWords();
  };

  const resetFilters = () => {
    setKeyword("");
    setPage(1);
  };

  // 打开导出确认弹窗
  const handleExport = () => {
    setExportDialogOpen(true);
  };

  // 执行实际导出操作
  const confirmExport = async () => {
    setProcessingExport(true);
    try {
      toast.success("导出成功", {
        description: "单词列表已成功导出",
      });
      setExportDialogOpen(false);
    } catch (e) {
      console.error(e);
      toast.error("导出失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setProcessingExport(false);
    }
  };

  // 播放音频
  const playAudio = (audioUrl: string) => {
    if (!audioUrl) {
      toast.error("该单词暂无音频");
      return;
    }
    const audio = new Audio(audioUrl);
    audio.play().catch((e) => {
      console.error(e);
      toast.error("音频播放失败");
    });
  };

  // 查看详情
  const handleViewDetail = (word: WordItem) => {
    setSelectedWord(word);
    setDetailDialogOpen(true);
  };

  // 打开编辑图片弹窗
  const handleEditImage = (word: WordItem) => {
    setEditingWord(word);
    // 设置初始图片值
    setImageEditValue(word.pic_name);
    setEditDialogOpen(true);
  };

  // 提交图片更新
  const handleSubmitImage = async () => {
    if (!editingWord) return;

    // 如果图片没有改变，直接关闭弹窗
    if (!imageEditValue) {
      toast.error("请先上传图片");
      return;
    }

    // 如果图片没有改变，直接关闭弹窗
    if (imageEditValue === editingWord.pic_name) {
      setEditDialogOpen(false);
      return;
    }

    setUploadingImage(true);
    try {
      await wordService.updateWordImage(editingWord.id, imageEditValue);

      toast.success("图片更新成功");
      setEditDialogOpen(false);
      fetchWords(); // 刷新列表
    } catch (e) {
      console.error(e);
      toast.error("图片更新失败", {
        description: e instanceof Error ? e.message : "请稍后重试",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold tracking-tight">单词管理</h2>
      </div>

      {/* 筛选区 */}
      <div className="rounded-lg border p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* 单词搜索 */}
          <div className="flex flex-col gap-2">
            <label className="text-sm text-gray-600">单词搜索</label>
            <div className="flex gap-2">
              <Input
                placeholder="请输入单词（支持模糊搜索）"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                className="flex-1"
              />
            </div>
          </div>

          {/* 操作按钮组 */}
          <div className="flex flex-col gap-2 justify-end">
            <label className="text-sm text-gray-600 opacity-0">操作</label>
            <div className="flex gap-2 justify-end">
              <Button className="w-24" variant="outline" onClick={resetFilters}>
                重置
              </Button>
              <Button className="w-24" onClick={handleSearch}>
                <Search className="mr-2 h-4 w-4" />
                查询
              </Button>
              <Button
                className="w-24"
                variant="secondary"
                onClick={handleExport}
              >
                <Download className="mr-2 h-4 w-4" />
                导出
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* 列表：可滚动容器 */}
      <div className="rounded-md border overflow-auto max-h-[60vh]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="whitespace-nowrap">单词</TableHead>
              <TableHead className="whitespace-nowrap">音标</TableHead>
              <TableHead className="whitespace-nowrap">释义</TableHead>
              <TableHead className="whitespace-nowrap">CEFR等级</TableHead>
              <TableHead className="whitespace-nowrap">词汇等级</TableHead>
              <TableHead className="whitespace-nowrap">标签</TableHead>
              <TableHead className="whitespace-nowrap">音频</TableHead>
              <TableHead className="whitespace-nowrap">创建时间</TableHead>
              <TableHead className="whitespace-nowrap">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  <div className="flex justify-center items-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
                  </div>
                </TableCell>
              </TableRow>
            ) : words.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="h-24 text-center">
                  没有找到单词记录
                </TableCell>
              </TableRow>
            ) : (
              words.map((word) => (
                <TableRow key={word.id}>
                  <TableCell className="font-medium whitespace-nowrap">
                    {word.vocab}
                    {word.different_spellings && (
                      <span className="text-xs text-gray-500 ml-1">
                        ({word.different_spellings})
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {word.phonetic || "-"}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {word.definition || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {word.cefr ? (
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          cefrColors[word.cefr] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {word.cefr}
                      </span>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {word.vocab_level || "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex gap-1 flex-wrap">
                      {word.ket && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                          KET
                        </span>
                      )}
                      {word.pet && (
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 rounded text-xs">
                          PET
                        </span>
                      )}
                      {word.college && (
                        <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded text-xs">
                          大学
                        </span>
                      )}
                      {word.middle && (
                        <span className="px-2 py-0.5 bg-orange-50 text-orange-700 rounded text-xs">
                          中学
                        </span>
                      )}
                      {!word.ket &&
                        !word.pet &&
                        !word.college &&
                        !word.middle && (
                          <span className="text-gray-400 text-xs">-</span>
                        )}
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {word.audio_url ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => playAudio(word.audio_url)}
                      >
                        <Volume2 className="h-4 w-4" />
                      </Button>
                    ) : (
                      <span className="text-gray-400 text-xs">无</span>
                    )}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {word.created_at
                      ? new Date(word.created_at).toLocaleString()
                      : "-"}
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <div className="flex gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetail(word)}
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        详情
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEditImage(word)}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        编辑图片
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* 查看详情弹窗 */}
      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => !open && setDetailDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>单词详情</DialogTitle>
          </DialogHeader>

          {selectedWord && (
            <div className="space-y-4 py-2">
              {/* 单词图片 */}
              {selectedWord.pic_name && (
                <div className="flex justify-center">
                  <img
                    src={formatImageUrl(selectedWord.pic_name)}
                    alt={selectedWord.vocab}
                    className="max-w-full max-h-64 rounded-lg object-contain"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    单词
                  </label>
                  <p className="mt-1 text-lg font-semibold">
                    {selectedWord.vocab}
                  </p>
                </div>
                {selectedWord.different_spellings && (
                  <div>
                    <label className="text-sm font-medium text-gray-600">
                      不同拼写
                    </label>
                    <p className="mt-1">{selectedWord.different_spellings}</p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    音标
                  </label>
                  <p className="mt-1">{selectedWord.phonetic || "-"}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    音频
                  </label>
                  <div className="mt-1">
                    {selectedWord.audio_url ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => playAudio(selectedWord.audio_url)}
                      >
                        <Volume2 className="h-4 w-4 mr-2" />
                        播放音频
                      </Button>
                    ) : (
                      <span className="text-gray-400">无音频</span>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">
                  释义
                </label>
                <p className="mt-1 whitespace-pre-wrap">
                  {selectedWord.definition || "-"}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    CEFR等级
                  </label>
                  <div className="mt-1">
                    {selectedWord.cefr ? (
                      <span
                        className={`px-3 py-1 rounded-full text-sm font-medium inline-block ${
                          cefrColors[selectedWord.cefr] ||
                          "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {selectedWord.cefr}
                      </span>
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    词汇等级
                  </label>
                  <p className="mt-1">{selectedWord.vocab_level || "-"}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-600">
                  标签
                </label>
                <div className="mt-1 flex gap-2 flex-wrap">
                  {selectedWord.ket && (
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      KET
                    </span>
                  )}
                  {selectedWord.pet && (
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      PET
                    </span>
                  )}
                  {selectedWord.college && (
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      大学
                    </span>
                  )}
                  {selectedWord.middle && (
                    <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm">
                      中学
                    </span>
                  )}
                  {!selectedWord.ket &&
                    !selectedWord.pet &&
                    !selectedWord.college &&
                    !selectedWord.middle && (
                      <span className="text-gray-400">无</span>
                    )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    创建时间
                  </label>
                  <p className="mt-1 text-sm">
                    {selectedWord.created_at
                      ? new Date(selectedWord.created_at).toLocaleString()
                      : "-"}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    更新时间
                  </label>
                  <p className="mt-1 text-sm">
                    {selectedWord.updated_at
                      ? new Date(selectedWord.updated_at).toLocaleString()
                      : "-"}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    词汇ID
                  </label>
                  <p className="mt-1 text-sm text-gray-500">
                    {selectedWord.vocabulary_id}
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">
                    UUID
                  </label>
                  <p className="mt-1 text-sm text-gray-500 truncate">
                    {selectedWord.uuid}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <DialogClose asChild>
              <Button variant="outline">关闭</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>

      {/* 编辑图片弹窗 */}
      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => !open && setEditDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-2xl bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>编辑单词图片</DialogTitle>
            <DialogDescription>
              为单词 "
              <span className="font-semibold">{editingWord?.vocab}</span>"
              更新图片
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <OSSUpload
              value={imageEditValue ? [imageEditValue] : []}
              accept="image/*"
              maxCount={1}
              maxSize={5}
              folder="Vocabulary_pics/"
              fileNamePrefix={editingWord?.vocab || "word"}
              placeholder="上传单词图片"
              aspectRatio="square"
              onChange={(fileUrls) => {
                if (fileUrls.length === 0) {
                  setImageEditValue("");
                  return;
                }
                const fileUrl = fileUrls[0];
                if (!fileUrl) {
                  setImageEditValue("");
                  return;
                }
                // 提取文件名（去除 folder 前缀）
                const fileName = fileUrl.split("/").reverse()[0];
                setImageEditValue(fileName);
              }}
            />
          </div>

          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={handleSubmitImage} disabled={uploadingImage}>
              {uploadingImage ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  更新中...
                </>
              ) : (
                "确认更新"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 导出确认弹窗 */}
      <Dialog
        open={exportDialogOpen}
        onOpenChange={(open) => !open && setExportDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>确认导出单词列表</DialogTitle>
            <DialogDescription>
              将当前筛选条件下的单词列表导出为Excel文件
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="bg-gray-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">导出记录数量:</span>
                <span className="font-medium text-blue-600">{total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">筛选条件:</span>
                <span className="text-gray-600">
                  {keyword ? `关键词: ${keyword}` : "所有单词"}
                </span>
              </div>
            </div>

            <p className="text-sm text-gray-500">
              导出的Excel文件将包含所有当前筛选条件下的单词数据
            </p>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button onClick={confirmExport} disabled={processingExport}>
              {processingExport ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
                  导出中...
                </>
              ) : (
                <>
                  <Download className="mr-2 h-4 w-4" />
                  确认导出
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {!loading && words.length > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-500 whitespace-nowrap">
            共 {total} 个单词，第 {page} 页 / 共 {totalPages} 页
          </div>
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => page > 1 && setPage(page - 1)}
                  className={page <= 1 ? "pointer-events-none opacity-50" : ""}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) pageNum = i + 1;
                else if (page <= 3) pageNum = i + 1;
                else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
                else pageNum = page - 2 + i;
                return (
                  <PaginationItem key={i}>
                    <PaginationLink
                      isActive={page === pageNum}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </PaginationLink>
                  </PaginationItem>
                );
              })}
              <PaginationItem>
                <PaginationNext
                  onClick={() => page < totalPages && setPage(page + 1)}
                  className={
                    page >= totalPages ? "pointer-events-none opacity-50" : ""
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}
    </div>
  );
}
